import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { useShopperAuth } from "../../context/ShopperAuthContext";
import { formatOrderDelivery, orderFulfillmentType } from "../../lib/orderFulfillment";
import { storePath } from "../../lib/storeApi";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-blue-100 text-blue-800",
  shipped: "bg-purple-100 text-purple-800",
  delivered: "bg-green-100 text-green-800",
  completed: "bg-slate-100 text-slate-800",
  cancelled: "bg-red-100 text-red-800",
};

const ACTIVE_STATUSES = new Set(["pending", "confirmed", "shipped", "delivered"]);

function isActiveOrder(order) {
  return ACTIVE_STATUSES.has(String(order?.status || "").toLowerCase());
}

function customerOrderGroupKey(order) {
  if (!isActiveOrder(order)) return "";
  if (orderFulfillmentType(order) === "offline") {
    return `table:${String(order.table_number || formatOrderDelivery(order) || "in-store").trim().toLowerCase()}`;
  }
  return "";
}

function mergeCustomerActiveOrders(orders) {
  const grouped = new Map();
  const separate = [];

  (orders || []).forEach((order) => {
    const key = customerOrderGroupKey(order);
    if (!key) {
      separate.push(order);
      return;
    }
    if (!grouped.has(key)) {
      grouped.set(key, {
        ...order,
        items: [...(order.items || [])],
        total: Number(order.total || 0),
        subtotal: Number(order.subtotal || order.total || 0),
        _sourceOrderNos: [order.order_no].filter(Boolean),
        _currentBill: true,
      });
      return;
    }
    const target = grouped.get(key);
    target.items = [...(target.items || []), ...(order.items || [])];
    target.total = Number(target.total || 0) + Number(order.total || 0);
    target.subtotal = Number(target.subtotal || 0) + Number(order.subtotal || order.total || 0);
    target._sourceOrderNos = [...(target._sourceOrderNos || []), order.order_no].filter(Boolean);
    target.updated_at = [target.updated_at, order.updated_at].filter(Boolean).sort().pop() || target.updated_at;
    if (new Date(order.created_at || 0) < new Date(target.created_at || 0)) target.created_at = order.created_at;
  });

  return [...grouped.values(), ...separate].sort((a, b) => String(b.updated_at || b.created_at || "").localeCompare(String(a.updated_at || a.created_at || "")));
}

export default function StoreOrders() {
  const { api, clientRef, resolvedHost, shopper, loading: authLoading } = useShopperAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const loginPath = storePath(clientRef, "login", resolvedHost);
  const homePath = storePath(clientRef, "", resolvedHost);
  const displayOrders = useMemo(() => mergeCustomerActiveOrders(orders), [orders]);

  const loadOrders = useCallback(async () => {
    try {
      const { data } = await api.get("/orders");
      setOrders(data);
    } catch {
      setOrders([]);
    }
  }, [api]);

  useEffect(() => {
    if (authLoading || !shopper || shopper === false) return;
    loadOrders();
    const timer = setInterval(loadOrders, 15000);
    return () => clearInterval(timer);
  }, [shopper, authLoading, loadOrders]);

  if (authLoading) return <div className="text-center py-20">Loading...</div>;
  if (!shopper || shopper === false) {
    return (
      <div className="bg-white rounded-xl border p-10 text-center max-w-lg mx-auto">
        <p className="mb-4">Sign in to view your orders.</p>
        <Button onClick={() => navigate(loginPath)}>Sign in</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold">Your Orders</h1>
      {orders.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#D5D9D9] p-10 text-center">
          <p className="text-[#565959]">No orders yet.</p>
          <Link to={homePath} className="text-[#007185] text-sm mt-2 inline-block">Start shopping</Link>
        </div>
      ) : (
        displayOrders.map((order) => (
          <div key={order.id} className="bg-white rounded-xl border border-[#D5D9D9] p-5">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="font-bold">{order._currentBill ? `Current bill - ${formatOrderDelivery(order)}` : order.order_no}</div>
                  {order._currentBill && <span className="rounded bg-[#111827] px-2 py-1 text-[10px] font-bold uppercase text-white">Live</span>}
                </div>
                <div className="text-xs text-[#565959]">{new Date(order.created_at).toLocaleString()}</div>
                {order._sourceOrderNos?.length > 1 && (
                  <div className="mt-1 text-xs text-[#565959]">
                    Includes {order._sourceOrderNos.length} order entries: {order._sourceOrderNos.join(", ")}
                  </div>
                )}
              </div>
              <span className={`text-xs font-semibold uppercase px-2 py-1 rounded ${STATUS_COLORS[order.status] || STATUS_COLORS.pending}`}>
                {order.status}
              </span>
            </div>
            <div className="space-y-2">
              {(order.items || []).map((item, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span>{item.name} x {item.qty}</span>
                  <span>₹ {Number(item.line_total || 0).toLocaleString()}</span>
                </div>
              ))}
            </div>
            <div className="text-sm text-[#565959] mt-2">
              {orderFulfillmentType(order) === "offline" ? "Table" : "Address"}: {formatOrderDelivery(order)}
            </div>
            <div className="border-t border-[#EEE] mt-3 pt-3 flex justify-between font-bold">
              <span>Total</span>
              <span>₹ {Number(order.total || 0).toLocaleString()}</span>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
